import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { History, RefreshCw, User, Filter, BarChart3, Calendar, TrendingUp } from 'lucide-react';
import { parseUtc, fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const ACTION_LABELS = {
  'recruiter.send_agreement': { label: 'sent agreement to', tone: 'sky' },
  'recruiter.activate': { label: 'activated', tone: 'emerald' },
  'recruiter.delete': { label: 'deleted', tone: 'rose' },
  'recruiter.status_agreement_signed': { label: 'synced signed status of', tone: 'violet' },
  'recruiter.status_declined': { label: 'marked as declined', tone: 'rose' },
  'recruiter.status_voided': { label: 'voided agreement of', tone: 'rose' },
  'staff.create': { label: 'invited new staff', tone: 'sky' },
  'staff.update': { label: 'updated staff', tone: 'amber' },
  'staff.delete': { label: 'removed staff', tone: 'rose' },
  'staff.resend_invite': { label: 'resent invite to', tone: 'amber' },
  'lead.send_sms': { label: 'sent SMS to', tone: 'sky' },
  'lead.delete': { label: 'deleted lead', tone: 'rose' },
  'student.delete': { label: 'deleted student', tone: 'rose' },
};

const TONE_CLASSES = {
  sky: 'bg-sky-50 text-sky-700 border-sky-100',
  emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  rose: 'bg-rose-50 text-rose-700 border-rose-100',
  violet: 'bg-violet-50 text-violet-700 border-violet-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  default: 'bg-gray-50 text-gray-700 border-gray-100',
};

function timeAgo(iso) {
  const dt = parseUtc(iso);
  if (!dt) return '—';
  const diff = Math.floor((Date.now() - dt.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return dt.toLocaleDateString('en-CA', { timeZone: 'America/Vancouver' });
}

export default function ActivityLog() {
  const [tab, setTab] = useState('feed'); // feed | team
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actorFilter, setActorFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [teamStats, setTeamStats] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [windowDays, setWindowDays] = useState(7);
  const [roleFilter, setRoleFilter] = useState('all');

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    if (tab === 'team') fetchTeamStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, windowDays, roleFilter]);

  async function fetchTeamStats() {
    setTeamLoading(true);
    try {
      const url = `${API}/api/admin/activity-log/team-stats?days=${windowDays}${roleFilter !== 'all' ? `&role=${roleFilter}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) setTeamStats(await res.json());
      else setTeamStats(null);
    } catch { setTeamStats(null); } finally { setTeamLoading(false); }
  }

  async function fetchEntries() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/activity-log?limit=200`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setEntries(d.entries || []);
      }
    } catch {/* ignore */} finally { setLoading(false); }
  }

  const actors = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => map.set(e.actor_id, { id: e.actor_id, name: e.actor_name, email: e.actor_email }));
    return Array.from(map.values());
  }, [entries]);

  const actionTypes = useMemo(() => {
    const set = new Set(entries.map((e) => e.action));
    return Array.from(set);
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (actorFilter !== 'all' && e.actor_id !== actorFilter) return false;
      if (actionFilter !== 'all' && e.action !== actionFilter) return false;
      return true;
    });
  }, [entries, actorFilter, actionFilter]);

  return (
    <div className="space-y-6" data-testid="activity-log-tab">
      {/* Tab switcher */}
      <div className="inline-flex bg-white rounded-full border border-tge-navy/10 p-1 text-xs">
        <button
          onClick={() => setTab('feed')}
          data-testid="activity-tab-feed"
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full transition ${tab === 'feed' ? 'bg-tge-navy text-white' : 'text-tge-navy/60 hover:text-tge-navy'}`}
        >
          <History size={12} /> Activity feed
        </button>
        <button
          onClick={() => setTab('team')}
          data-testid="activity-tab-team"
          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full transition ${tab === 'team' ? 'bg-tge-navy text-white' : 'text-tge-navy/60 hover:text-tge-navy'}`}
        >
          <BarChart3 size={12} /> Team activity
        </button>
      </div>

      {tab === 'team' ? (
        <TeamActivityPanel
          data={teamStats}
          loading={teamLoading}
          windowDays={windowDays}
          setWindowDays={setWindowDays}
          roleFilter={roleFilter}
          setRoleFilter={setRoleFilter}
          onRefresh={fetchTeamStats}
        />
      ) : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { l: 'Total events', v: entries.length, c: 'text-tge-navy' },
          { l: 'Active users', v: actors.length, c: 'text-tge-gold' },
          { l: 'Action types', v: actionTypes.length, c: 'text-emerald-600' },
          { l: 'Last 24h', v: entries.filter((e) => Date.now() - new Date(e.created_at).getTime() < 86400000).length, c: 'text-sky-600' },
        ].map((s, i) => (
          <motion.div key={s.l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="bg-white rounded-2xl p-5 border border-tge-navy/5">
            <div className="text-[11px] uppercase tracking-wider text-tge-muted">{s.l}</div>
            <div className={`font-serif-display text-3xl mt-2 ${s.c}`}>{s.v}</div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
        <div className="px-6 py-5 border-b border-tge-navy/5 flex flex-wrap items-center gap-3 justify-between">
          <h3 className="font-serif-display text-xl text-tge-navy flex items-center gap-2">
            <History size={18} className="text-tge-gold" /> Activity feed
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs">
              <Filter size={12} className="text-tge-muted" />
              <select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}
                className="bg-tge-cream-soft border border-tge-cream rounded-full px-3 py-1.5 text-tge-navy outline-none focus:border-tge-gold"
                data-testid="activity-filter-actor">
                <option value="all">All users</option>
                {actors.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}
                className="bg-tge-cream-soft border border-tge-cream rounded-full px-3 py-1.5 text-tge-navy outline-none focus:border-tge-gold"
                data-testid="activity-filter-action">
                <option value="all">All actions</option>
                {actionTypes.map((a) => <option key={a} value={a}>{ACTION_LABELS[a]?.label || a}</option>)}
              </select>
            </div>
            <button onClick={fetchEntries} className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft" data-testid="activity-refresh-btn">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-tge-muted text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-tge-muted">
            <History size={28} className="mx-auto mb-2 opacity-40" />
            <p>No activity yet. Actions taken in the dashboard will appear here.</p>
          </div>
        ) : (
          <ol className="divide-y divide-tge-cream">
            {filtered.map((e) => {
              const meta = ACTION_LABELS[e.action] || { label: e.action, tone: 'default' };
              return (
                <li key={e.id} className="px-6 py-4 flex items-start gap-4 hover:bg-tge-cream-soft/30 transition" data-testid="activity-row">
                  <div className="w-9 h-9 rounded-full bg-tge-gold/20 text-tge-gold flex items-center justify-center font-serif-display text-sm shrink-0 mt-0.5">
                    {e.actor_name?.charAt(0)?.toUpperCase() || <User size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-tge-navy">
                      <span className="font-medium">{e.actor_name || e.actor_email}</span>
                      <span className="text-tge-muted text-[10px] uppercase tracking-wider ml-2 px-1.5 py-0.5 rounded bg-tge-cream-soft">{e.actor_role}</span>
                      <span className="text-tge-muted"> {meta.label} </span>
                      <span className={`inline-block text-[12px] px-2 py-0.5 rounded-md border ${TONE_CLASSES[meta.tone] || TONE_CLASSES.default}`}>
                        {e.resource_label || e.resource_id || '—'}
                      </span>
                    </p>
                    {e.metadata && Object.keys(e.metadata).length > 0 && (
                      <div className="mt-1.5 text-[11px] text-tge-muted truncate">
                        {Object.entries(e.metadata).slice(0, 3).map(([k, v]) => (
                          <span key={k} className="mr-3">
                            <span className="font-medium">{k}:</span> {String(v).slice(0, 60)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-[11px] text-tge-muted shrink-0 mt-1" title={fmtDateTime(e.created_at) + ' (Vancouver)'}>
                    {timeAgo(e.created_at)}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
      </>
      )}
    </div>
  );
}

function TeamActivityPanel({ data, loading, windowDays, setWindowDays, roleFilter, setRoleFilter, onRefresh }) {
  const fmtDate = (iso) => fmtDateTime(iso);
  const initials = (name) =>
    (name || '?').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '·';
  const ROLE_COLOR = {
    admin: 'bg-tge-navy text-white',
    manager: 'bg-blue-600 text-white',
    counsellor: 'bg-emerald-600 text-white',
    staff: 'bg-emerald-600 text-white',
  };
  const topActions = data
    ? Object.entries(data.by_action || {}).sort((a, b) => b[1] - a[1]).slice(0, 6)
    : [];
  const maxAction = topActions[0]?.[1] || 1;
  const actors = data?.actors || [];

  return (
    <div className="space-y-5" data-testid="team-activity-panel">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex items-center gap-1 bg-white border border-tge-navy/10 rounded-full p-1">
          {[1, 7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              data-testid={`team-window-${d}`}
              className={`text-[11px] px-3 py-1 rounded-full transition ${windowDays === d ? 'bg-tge-navy text-white' : 'text-tge-navy/60 hover:text-tge-navy'}`}
            >
              {d === 1 ? '24h' : `${d}d`}
            </button>
          ))}
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="text-xs bg-white border border-tge-navy/10 rounded-full px-3 py-1.5 text-tge-navy outline-none focus:border-tge-gold"
          data-testid="team-role-filter"
        >
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="counsellor">Counsellor</option>
        </select>
        <div className="flex-1" />
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 text-xs text-tge-muted hover:text-tge-navy transition px-3 py-1.5 rounded-full border border-tge-navy/10 hover:border-tge-navy/30 hover:bg-tge-cream-soft"
        >
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="text-center py-16 text-tge-muted text-sm">Loading…</div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-2xl border border-tge-navy/5 text-center py-16">
          <BarChart3 size={32} className="mx-auto text-tge-navy/20 mb-2" />
          <div className="text-tge-navy/60 text-sm">No activity in this window.</div>
        </div>
      ) : (
        <>
          {/* Roll-up cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { l: 'Total events', v: data.total, c: 'text-tge-navy', Icon: TrendingUp },
              { l: 'Active people', v: data.actor_count, c: 'text-tge-gold', Icon: User },
              { l: 'Action types', v: Object.keys(data.by_action || {}).length, c: 'text-emerald-600', Icon: BarChart3 },
              { l: 'Window', v: windowDays === 1 ? '24h' : `${windowDays}d`, c: 'text-sky-600', Icon: Calendar },
            ].map((s, i) => (
              <motion.div
                key={s.l}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-5 border border-tge-navy/5"
              >
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wider text-tge-muted">{s.l}</div>
                  <s.Icon size={14} className="text-tge-navy/30" />
                </div>
                <div className={`font-serif-display text-3xl mt-2 ${s.c}`}>{s.v}</div>
              </motion.div>
            ))}
          </div>

          {/* Top actions bar chart */}
          {topActions.length > 0 && (
            <div className="bg-white rounded-2xl p-5 border border-tge-navy/5">
              <div className="text-sm font-semibold text-tge-navy mb-3">Most frequent actions</div>
              <div className="space-y-2">
                {topActions.map(([action, count]) => (
                  <div key={action} className="flex items-center gap-3 text-xs">
                    <div className="w-44 text-tge-navy/70 font-mono truncate">{action}</div>
                    <div className="flex-1 bg-tge-cream-soft rounded-full h-3 overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-tge-navy to-blue-600 rounded-full transition-all"
                        style={{ width: `${(count / maxAction) * 100}%` }}
                      />
                    </div>
                    <div className="w-10 text-right font-semibold text-tge-navy">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-actor leaderboard */}
          <div className="bg-white rounded-2xl border border-tge-navy/5 overflow-hidden">
            <div className="px-5 py-4 border-b border-tge-navy/5">
              <div className="text-sm font-semibold text-tge-navy">Per-person leaderboard</div>
              <div className="text-[11px] text-tge-navy/50">Sorted by total actions in the selected window.</div>
            </div>
            <div className="divide-y divide-tge-cream">
              {actors.map((a, idx) => {
                const topUserActions = Object.entries(a.by_action || {}).sort((x, y) => y[1] - x[1]).slice(0, 4);
                return (
                  <div key={a.actor_id} className="px-5 py-4 hover:bg-tge-cream-soft/40 transition" data-testid={`team-actor-${a.actor_id}`}>
                    <div className="flex items-start gap-3">
                      <div className="text-[11px] font-mono text-tge-navy/40 w-6 mt-1 shrink-0">#{idx + 1}</div>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm shrink-0 ${ROLE_COLOR[a.actor_role] || 'bg-slate-500 text-white'}`}>
                        {initials(a.actor_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <div className="font-medium text-tge-navy text-sm truncate">{a.actor_name}</div>
                          <span className="text-[10px] uppercase tracking-wider text-tge-muted bg-tge-cream-soft px-1.5 py-0.5 rounded">{a.actor_role}</span>
                          <span className="text-[11px] text-tge-navy/50">{a.actor_email}</span>
                        </div>
                        <div className="text-[11px] text-tge-navy/50 mt-0.5">
                          Last active {fmtDate(a.last_at)} · {a.distinct_resources} distinct resources
                        </div>
                        {topUserActions.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {topUserActions.map(([k, v]) => (
                              <span key={k} className="text-[10px] bg-tge-cream-soft border border-tge-cream rounded-full px-2 py-0.5 text-tge-navy/70">
                                <span className="font-mono">{k}</span> · <span className="font-semibold text-tge-navy">{v}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-serif-display text-2xl text-tge-navy">{a.total}</div>
                        <div className="text-[10px] uppercase tracking-wider text-tge-muted">events</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
